import React, { useEffect, useMemo } from 'react';
import {
  bool,
  string,
  arrayOf,
  node
} from 'prop-types';

import { extend } from '@thd-nucleus/data-sources';
import { CancelModal } from './components/cancel-modal/cancel-modal';

import { FeatureSwitchContext } from './context/feature-switch-context';
import DeliveryProvider from './context/delivery-context';
import ReservationProvider from './context/reservation-context';
import ReservationLookupApiProvider from './context/ReservationLookupApiContext';
import ReservationUpdateApiProvider from './context/ReservationUpdateApiContext';
import MarketCheckApiProvider from './context/GetRentalMarketCheckContext';
import GetRentalConsumablesProvider from './context/GetRentalConsumablesContext';
import { ProductInformation } from './containers/product-information/product-information';
import { ReservationForm } from './containers/reservation-form/reservation-form';
import { ReservationModifyForm } from './containers/reservation-modify-form/reservation-modify-form';
import { DeliveryForm } from './containers/delivery-form/delivery-form';

import './rental-reservation-form.scss';

const RentalReservationFormProviders = ({
  whitelistedStores,
  blacklistedItems,
  rentalEstimateEnabled,
  contractTermsEnabled,
  damageProtectionEnabled,
  FDAv4Enabled,
  estimateGraphQlEnabled,
  graphQlEnabled,
  children
}) => {

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('rental-reservation-form-providers.ready');
  }, []);

  const featureSwitchContextValue = useMemo(() => ({
    whitelistedStores,
    blacklistedItems,
    rentalEstimateEnabled,
    contractTermsEnabled,
    damageProtectionEnabled,
    FDAv4Enabled,
    estimateGraphQlEnabled,
    graphQlEnabled
  }), [
    whitelistedStores,
    blacklistedItems,
    rentalEstimateEnabled,
    contractTermsEnabled,
    damageProtectionEnabled,
    FDAv4Enabled,
    estimateGraphQlEnabled,
    graphQlEnabled
  ]);

  return (
    <FeatureSwitchContext.Provider value={featureSwitchContextValue}>
      <ReservationProvider>
        {children}
      </ReservationProvider>
    </FeatureSwitchContext.Provider>
  );
};

RentalReservationFormProviders.displayName = 'RentalReservationFormProviders';

RentalReservationFormProviders.propTypes = {
  whitelistedStores: arrayOf(string),
  blacklistedItems: arrayOf(string),
  rentalEstimateEnabled: bool,
  contractTermsEnabled: bool,
  damageProtectionEnabled: bool,
  FDAv4Enabled: bool,
  estimateGraphQlEnabled: bool,
  graphQlEnabled: bool,
  children: node.isRequired
};

RentalReservationFormProviders.defaultProps = {
  whitelistedStores: [],
  blacklistedItems: [],
  rentalEstimateEnabled: false,
  contractTermsEnabled: false,
  damageProtectionEnabled: false,
  FDAv4Enabled: false,
  graphQlEnabled: false,
  estimateGraphQlEnabled: false
};

// modify providers
const RentalReservationModifyFormProviders = ({
  reservationId,
  uuid,
  storeId,
  FDAv4Enabled,
  graphQlEnabled,
  estimateGraphQlEnabled,
  children
}) => {

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('rental-reservation-modify-form-providers.ready');
  }, []);

  const featureSwitchContextValue = useMemo(() => ({
    FDAv4Enabled,
    estimateGraphQlEnabled,
    graphQlEnabled
  }), [FDAv4Enabled, estimateGraphQlEnabled, graphQlEnabled]);

  return (
    <FeatureSwitchContext.Provider value={featureSwitchContextValue}>
      <ReservationProvider reservationId={reservationId} uuid={uuid} storeId={storeId}>
        <ReservationLookupApiProvider>
          <ReservationUpdateApiProvider>
            {children}
          </ReservationUpdateApiProvider>
        </ReservationLookupApiProvider>
      </ReservationProvider>
    </FeatureSwitchContext.Provider>
  );
};

RentalReservationModifyFormProviders.propTypes = {
  reservationId: string.isRequired,
  uuid: string.isRequired,
  storeId: string.isRequired,
  FDAv4Enabled: bool,
  estimateGraphQlEnabled: bool,
  graphQlEnabled: bool,
  children: node.isRequired
};

RentalReservationModifyFormProviders.defaultProps = {
  FDAv4Enabled: false,
  estimateGraphQlEnabled: false,
  graphQlEnabled: false
};

RentalReservationModifyFormProviders.dataModel = extend(ReservationProvider);

// delivery providers
const RentalDeliveryFormProviders = ({
  itemId,
  children
}) => {

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('rental-delivery-form-providers.ready');
  }, []);

  return (
    <DeliveryProvider itemId={itemId}>
      <GetRentalConsumablesProvider>
        <MarketCheckApiProvider>
          {children}
        </MarketCheckApiProvider>
      </GetRentalConsumablesProvider>
    </DeliveryProvider>
  );
};

RentalDeliveryFormProviders.displayName = 'RentalDeliveryFormProviders';

RentalDeliveryFormProviders.propTypes = {
  itemId: string.isRequired,
  children: node.isRequired
};

RentalDeliveryFormProviders.dataModel = extend(DeliveryProvider, GetRentalConsumablesProvider, MarketCheckApiProvider);

export {
  CancelModal,
  ProductInformation,
  ReservationForm,
  RentalReservationFormProviders,
  ReservationModifyForm,
  RentalReservationModifyFormProviders,
  DeliveryForm,
  RentalDeliveryFormProviders
};
